/// <amd-module name="Repos/PullRequestMentionAutocomplete/PullRequestAutocompleteProvider" />
import { htmlEncode, SearchBehavior, searchString } from "Mention/Autocomplete/AutocompleteUtility";
import { IAutocompleteProvider, ISuggestionSearchResult } from "Mention/Autocomplete/Contracts";
import * as React from "react";
import * as ReactDOM from "react-dom";
import * as Resources from "Repos/PullRequestMentionAutocomplete/Resources";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";
import { ObservableValue } from "VSS/Core/Observable";
import { IMarkdownTransformContext } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import { Services, VssService } from "VSS/Platform/Context";
import { Icon } from "VSSUI/Icon";
import { Link } from "VSSUI/Link";
import { IMenuItem } from "VSSUI/Menu";
import { Observer } from "VSSUI/Observer";
import { Tooltip } from "VSSUI/TooltipEx";
import { PullRequestMention } from "./Contracts";
import { PullRequestAutocompleteCommand } from "./PullRequestAutocompleteCommand";
import { compareMentions, union } from "./PullRequestAutocompleteUtility";
import { getPullRequestMentionProviderService } from "./PullRequestMentionDataProvider";

const pullRequestIDPattern = /pullrequest\/(\d+)/i;

class PullRequestAutocompleteProviderService extends VssService implements IAutocompleteProvider, IMenuService {
    public readonly shortcutCharacter = "!";

    public search(term: string): Promise<ISuggestionSearchResult<PullRequestMention>[]> {
        return this._getSuggestions(term);
    }

    public getSuggestions(): Promise<ISuggestionSearchResult<PullRequestMention>[]> {
        return this._getSuggestions("");
    }

    public getReplacementText(item: ISuggestionSearchResult<PullRequestMention>): string {
        return `${this.shortcutCharacter}${item.original.id}`;
    }

    public getReplacementHtml(item: ISuggestionSearchResult<PullRequestMention>): string {
        return `<a href="${item.original.url}" data-vss-mention="version:1.0">PR ${item.original.id}: ${item.original.title}</a>`;
    }

    public getMenuItems(): IMenuItem[] {
        return [new PullRequestAutocompleteCommand()];
    }

    /**
     * Given a Pull Request mention, this will fetch the pull request to get its title
     * and then render the mention so that text content like !123 will become a nice looking PR link
     */
    public transformMarkdown(transformContext: IMarkdownTransformContext, ignoreMatch?: (matchIndex: number) => boolean): void {
        transformContext.markup = transformContext.markup.replace(/\!([0-9]*)/g, (fullMatch: string, pullRequestId: string, offset: number) => {
            if (!pullRequestId) {
                return fullMatch;
            }

            if (ignoreMatch && ignoreMatch(offset)) {
                return fullMatch;
            }

            const prMention = new ObservableValue<PullRequestMention | null>(null);
            getPullRequestMentionProviderService(this.pageContext)
                .getPullRequest(parseInt(pullRequestId))
                .then(p => (prMention.value = p));

            return transformContext.createReplacement(
                <Observer prMention={prMention}>
                    {({ prMention }: { prMention: PullRequestMention | null }) => {
                        if (prMention) {
                            return <PullRequestMention href={prMention.url} pullRequestId={prMention.id} title={prMention.title} />;
                        } else {
                            return fullMatch;
                        }
                    }}
                </Observer>,
                "span"
            );
        });
    }

    /**
     * Given an html document containing mentions as raw anchor tags, this function will replace those mentions
     * with nice-looing pull request links.
     */
    public transformHtml(element: HTMLElement): Promise<void> {
        element.querySelectorAll("a[data-vss-mention]").forEach(async (a: HTMLAnchorElement) => {
            if (!a.getAttribute("data-vss-mention") || !a.getAttribute("href")) {
                return;
            }

            const href = a.getAttribute("href")!;
            const prId = pullRequestIDPattern.exec(href)?.[1];
            if (!prId || !parseInt(prId)) {
                return;
            }
            // Everything after the first colon is the title.
            const prTitle = a.innerText.substr(a.innerText.indexOf(':') + 1).trim();

            const replacement = <PullRequestMention href={href} pullRequestId={prId} title={prTitle} />;

            const newRoot = document.createElement("span");
            a.replaceWith(newRoot);
            ReactDOM.render(replacement, newRoot);
        });
        return Promise.resolve();
    }

    public renderSuggestion(item: ISuggestionSearchResult<PullRequestMention>): JSX.Element {
        const { original, highlighted } = item;

        const typeAndIdHtml = {
            __html: filterXSS(highlighted ? `PR ${highlighted.id}` : `PR ${original.id}`, xssStyleDefaultOptions)
        };

        const titleHtml = {
            __html: filterXSS(highlighted ? highlighted.title : original.title, xssStyleDefaultOptions)
        };

        return (
            <div className="flex-row">
                <Icon className="pr-icon" iconName="BranchPullRequest" />
                <span className="pr-type-id" dangerouslySetInnerHTML={typeAndIdHtml} />
                <span className="content-splitter">: </span>
                <Tooltip text={original.title}>
                    <span className="flex-row text-ellipsis" dangerouslySetInnerHTML={titleHtml} />
                </Tooltip>
            </div>
        );
    }

    /**
     * Search for pull requests provided the given search term which might be a pull request id or might be a search term
     * When returning results, we provide both an 'original' and a 'highlighted'
     * The highlighted version of the title will bold to match the search term. Let's say I have a pull request called 'A shiny apple'
     * When searching for apple, the highlighted result will be 'A shiny <span class='font-weight-heavy'>apple</span>' to provider better rendering
     */
    private _getSuggestions(term: string): Promise<ISuggestionSearchResult<PullRequestMention>[]> {
        term = term.trim();
        const numberMatches = term.match(/^(\d+)$/);
        let providerPromise: Promise<ISuggestionSearchResult<PullRequestMention>[]>;

        // if we matched the number pattern such as !123, then look for pull request 123
        if (!!numberMatches) {
            const specificPromise = this._findPullRequestById(term);
            const searchPromise = this._searchPullRequests(term);
            providerPromise = Promise.all([specificPromise, searchPromise]).then(results => {
                const result = union(results[0], results[1], compareMentions);
                return result;
            });
        } else {
            // else if a search term was provided such as !apple, search all cached pull requests whose title contains 'apple'
            providerPromise = this._searchPullRequests(term).then((searchResult: ISuggestionSearchResult<PullRequestMention>[]) => {
                searchResult.forEach(result => {
                    result.highlighted = this._searchHighlightedMention(term, result.original);
                });

                return searchResult;
            });
        }

        return providerPromise;
    }

    private _findPullRequestById(id: string): Promise<ISuggestionSearchResult<PullRequestMention>[]> {
        const prId = parseInt(id);
        return getPullRequestMentionProviderService(this.pageContext)
            .getPullRequest(prId)
            .then((prMention: PullRequestMention | null) => {
                if (prMention) {
                    return [
                        {
                            original: prMention,
                            highlighted: this._searchHighlightedMention(id.toString(), prMention)
                        }
                    ];
                } else {
                    return [];
                }
            });
    }

    private _searchPullRequests(searchTerm: string): Promise<ISuggestionSearchResult<PullRequestMention>[]> {
        return getPullRequestMentionProviderService(this.pageContext).searchPullRequests(searchTerm);
    }

    private _searchHighlightedMention(query: string, pr: PullRequestMention): PullRequestMention {
        let highlightedPullRequest = pr;
        const highlightSubstitutionPattern = `<span class="font-weight-heavy">$&</span>`; // TODO: common constant
        const terms = query
            .split(/\s+/)
            .filter(term => !!term)
            .map(term => htmlEncode(term));
        const idSearchResult = searchString(pr.id.toString(), terms, SearchBehavior.Prefix, highlightSubstitutionPattern);
        const titleSearchResult = searchString(htmlEncode(pr.title), terms, SearchBehavior.Contains, highlightSubstitutionPattern);
        let allTermsFound = true;
        terms.forEach(term => {
            const foundTerm = idSearchResult.found[term] || titleSearchResult.found[term];
            if (!foundTerm) {
                allTermsFound = false;
            }
        });
        if (allTermsFound) {
            highlightedPullRequest = {
                id: pr.id,
                title: titleSearchResult.highlightedSource,
                url: pr.url
            };
        }
        return highlightedPullRequest;
    }
}

interface IPullRequestMentionProps {
    href: string;
    pullRequestId: string | number;
    title: string;
}

function PullRequestMention({ href, pullRequestId, title }: IPullRequestMentionProps) {
    return (
        <Link className="mention-widget-item mention-pr-link" href={href}>
            <Icon className="pr-icon margin-right-4" iconName="BranchPullRequest" />
            <span className="mention-widget-typeid">
                {Resources.PullRequestPRText} {pullRequestId}:{" "}
            </span>
            <span className="mention-widget-title">{title}</span>
        </Link>
    );
}

Services.add("pull-request-autocomplete-service", { serviceFactory: PullRequestAutocompleteProviderService });
